<template>
  <div class="flex flex-col min-h-screen bg-gray-150 p-2 dark:bg-gray-700">
    <div class="relative mb-2 mt-1 dark:bg-gray-600 rounded border dark:border-gray-600 border-gray-200">
      <div class="flex justify-between items-start mb-2 space-x-4 text-xs font-medium px-4 py-2">
        <div v-if="leaveToday.length" class="w-1/2 rounded-md">
          <div v-for="(i, idx) in leaveToday" :key="'today-' + idx" class="flex items-start mb-1 text-red-600">
            <div>{{ i.pasttoday }} {{ i.daypart }} {{ i.employee }} {{ i.leavetype }} {{ i.range }}</div>

            <div v-if="i.public_note" class="relative group"
              @mouseenter="hoveredLeaveIndex = idx; hoveredLeaveType = 'today'" @mouseleave="hoveredLeaveIndex = null">
              <svg class="w-5 h-5 text-blue-500 ml-2 cursor-pointer" xmlns="http://www.w3.org/2000/svg"
                fill="currentColor" viewBox="0 0 24 24">
                <path fill-rule="evenodd"
                  d="M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm9.408-5.5a1 1 0 1 0 0 2h.01a1 1 0 1 0 0-2h-.01ZM10 10a1 1 0 1 0 0 2h1v3h-1a1 1 0 1 0 0 2h4a1 1 0 1 0 0-2h-1v-4a1 1 0 0 0-1-1h-2Z"
                  clip-rule="evenodd" />
              </svg>

              <div v-if="hoveredLeaveIndex === idx && hoveredLeaveType === 'today'"
                class="absolute left-7 top-0 z-10 bg-gray-800 text-white text-xs border border-gray-800 rounded-md shadow-md px-2 py-1 w-48 whitespace-pre-line">
                {{ i.public_note }}
              </div>
            </div>
          </div>
        </div>
        <div v-else></div>

        <div v-if="leaveFuture.length" class="w-1/2 rounded-md text-right">
          <div v-for="(i, idx) in leaveFuture" :key="'future-' + idx"
            class="flex justify-end items-start mb-1 text-yellow-600">
            <div>{{ i.range }} {{ i.pasttoday }} {{ i.daypart }} {{ i.employee }} {{ i.leavetype }}</div>

            <div v-if="i.public_note" class="relative group"
              @mouseenter="hoveredLeaveIndex = idx; hoveredLeaveType = 'future'" @mouseleave="hoveredLeaveIndex = null">
              <svg class="w-5 h-5 text-blue-500 dark:text-blue ml-2 cursor-pointer" xmlns="http://www.w3.org/2000/svg"
                fill="currentColor" viewBox="0 0 24 24">
                <path fill-rule="evenodd"
                  d="M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm9.408-5.5a1 1 0 1 0 0 2h.01a1 1 0 1 0 0-2h-.01ZM10 10a1 1 0 1 0 0 2h1v3h-1a1 1 0 1 0 0 2h4a1 1 0 1 0 0-2h-1v-4a1 1 0 0 0-1-1h-2Z"
                  clip-rule="evenodd" />
              </svg>

              <div v-if="hoveredLeaveIndex === idx && hoveredLeaveType === 'future'"
                class="absolute right-6 top-4 z-10 bg-gray-800 text-white text-xs border border-gray-800 rounded-md shadow-md px-2 py-1 w-48 whitespace-pre-line text-left">
                {{ i.public_note }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Broadcasts management panel + composer (managers 10/20/30/80) -->
    <BroadcastManagePanel v-if="canManageBroadcasts" :reload-signal="broadcastReloadSignal"
      @active-companies="onActiveBroadcastCompanies" @edit="onEditBroadcast" />
    <BroadcastComposerModal :is-visible="showBroadcastComposer" :preset-client="composerPresetClient"
      :preset-ticket-id="composerPresetTicketId" :edit-broadcast="composerEditBroadcast"
      @close="showBroadcastComposer = false" @created="onBroadcastCreated" />

    <!-- Pagination row; Broadcast button sits at its right edge so it doesn't take its own row -->
    <div class="relative mb-3">
    <div v-if="canManageBroadcasts" class="absolute right-0 top-0">
      <button type="button"
        class="inline-flex items-center px-4 py-2 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-red-500 hover:bg-red-700 dark:bg-red-300 dark:hover:bg-red-600"
        @click="openBroadcastComposer">
        📢 Broadcast
      </button>
    </div>
    <div class="flex justify-center" v-if="totalTickets.length > 0">
      <button @click="changePage(currentPage - 1)"
        class="mx-1 px-4 py-2 text-xs font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
        :disabled="currentPage <= 1">
        &lt;
      </button>
      <button
        class="mx-1 px-4 py-2 text-xs font-medium text-gray-700 bg-white rounded-lg border border-blue-500 hover:bg-gray-50">
        {{ currentPage }}
      </button>
      <button @click="changePage(currentPage + 1)" v-if="currentPage < totalPages"
        class="mx-1 px-4 py-2 text-xs font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50">
        {{ currentPage + 1 }}
      </button>

      <button @click="changePage(currentPage + 1)"
        class="mx-1 px-4 py-2 text-xs font-medium text-gray-700 bg-white rounded-lg border border-gray-300 hover:bg-gray-50 disabled:opacity-50"
        :disabled="currentPage >= totalPages">
        &gt;
      </button>
    </div>
    <div v-else>
      <label class="block text-l font-medium text-gray-700 text-center dark:text-gray-100 mt-4 mb-4">No undelegated
        tickets at this time</label>
    </div>
    </div>
    <div v-if="currentTicket && currentTicket.jobs && currentTicket.jobs.length > 0"
      class="p-3 bg-red-100 border-2 border-red-500 rounded shadow-md dark:bg-red-800 dark:border-red-400 mb-4 cursor-pointer"
      @click="showJobsModal = true">
      <h3 class="text-xs font-medium text-gray-900 dark:text-gray-100">
        ⚠ There is already activity on this ticket — Click here to view.
      </h3>
    </div>

    <!-- Auto duplicate-of-known-issue flag when the client has a live broadcast -->
    <div v-if="currentTicketHasBroadcast"
      class="p-3 bg-red-50 border-2 border-red-500 rounded shadow-md dark:bg-red-800 dark:border-red-400 mb-4 flex items-center gap-2">
      <svg class="h-4 w-4 text-red-600 dark:text-red-200 flex-shrink-0" viewBox="0 0 20 20" fill="currentColor">
        <path d="M8.3 2.3a2 2 0 013.4 0l6 10.4A2 2 0 0116 15.7H4a2 2 0 01-1.7-3l6-10.4zM10 7a1 1 0 00-1 1v3a1 1 0 002 0V8a1 1 0 00-1-1zm0 7a1 1 0 100 2 1 1 0 000-2z" />
      </svg>
      <h3 class="text-xs font-medium text-gray-900 dark:text-gray-100">
        There is an <b>active broadcast</b> for this client — this ticket may be a duplicate of a known issue.
      </h3>
    </div>

    <div class="flex flex-col md:flex-row gap-2" :class="totalTickets.length > 0
      ? 'md:grid md:grid-cols-[3fr,3fr,1fr]'
      : 'flex flex-col md:flex-row gap-6'
      ">
      <!-- Ticket Details Form -->
      <div class="flex-1 border rounded-2xl px-6 py-6 bg-white rounded dark:bg-gray-600 dark:border-gray-600"
        v-if="currentTicket">
        <div class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-1">
            <div class="md:col-span-2">
              <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Submitted Date</label>
              <input type="datetime-local"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 dark:placeholder-gray-100"
                v-model="currentTicket.submitted" />
            </div>
            <div class="md:col-span-2" v-click-outside="closeTypeDropdown">
              <label class="block font-medium text-gray-700 text-xs dark:text-gray-100">Type</label>
              <div class="relative">
                <input type="text" @focus="dropdownState.type.visible = true"
                  @keydown.down.prevent="onKeyDown('type', 'down')" @keydown.up.prevent="onKeyDown('type', 'up')"
                  @keydown.enter.prevent="onEnter('type', setReqType)"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 dark:placeholder-gray-100"
                  placeholder="Type" v-model="currentTicket.type" />
                <button v-if="currentTicket.type != '' && currentTicket.tickettype_id != ''" @click="clearReqType"
                  class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500">
                  <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                      stroke-linejoin="round" />
                    <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </button>
              </div>
              <ul v-if="dropdownState.type.visible"
                class="absolute z-10 bg-white mt-1 rounded-md shadow-lg w-100 dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 scrollable-requester-list">
                <li v-for="(reqtype, index) in filteredReqTypes" :key="reqtype.id" @click="setReqType(reqtype)"
                  :ref="el => setDropdownItemRef(el, index, 'type')" :class="[
                    'cursor-pointer p-3',
                    dropdownState.type.index === index ? 'bg-blue-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  ]">
                  {{ reqtype.description }}
                </li>
              </ul>
            </div>
            <div class="md:col-span-2" v-click-outside="closeClientDropdown">
              <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Client</label>
              <div class="relative">
                <input type="text" @focus="dropdownState.client.visible = true"
                  @keydown.down.prevent="onKeyDown('client', 'down')" @keydown.up.prevent="onKeyDown('client', 'up')"
                  @keydown.enter.prevent="onEnter('client', setClient)"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 dark:placeholder-gray-100"
                  placeholder="Client Name" v-model="currentTicket.client.companyname" />
                <button
                  v-if="currentTicket.client && currentTicket.client.companyname != '' && currentTicket.company_id != ''"
                  @click="clearClient"
                  class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500">
                  <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                      stroke-linejoin="round" />
                    <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </button>
              </div>
              <ul v-if="dropdownState.client.visible"
                class="absolute z-10 bg-white mt-1 rounded-md shadow-lg w-100 dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 scrollable-requester-list">
                <li v-for="(client, index) in filteredClients" :key="client.id" @click="setClient(client)"
                  :ref="el => setDropdownItemRef(el, index, 'client')" :class="[
                    'cursor-pointer p-3',
                    dropdownState.client.index === index ? 'bg-blue-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                  ]">
                  {{ client.companyname }}
                </li>
              </ul>
            </div>
            <!-- <hr class="md:col-span-2 mt-4 border-t-2 border-gray-300"> -->
            <!-- ... other fields from the form in the picture ... -->
            <div class="md:col-span-2 mt-2">
              <div
                class="p-4 bg-white shadow-md rounded-md border-2 border-blue-900 relative dark:text-gray-100 dark:bg-gray-400 dark:border-gray-800">
                <div class="absolute top-0 left-0 right-0 bg-blue-900 px-3 py-1 flex items-center dark:bg-gray-800">
                  <div class="flex items-center space-x-2">
                    <h4 class="text-white font-bold text-xs">Requested By</h4>
                  </div>
                </div>
                <div class="md:col-span-2 mt-2">
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-1">
                    <div class="flex space-x-2">
                      <div class="flex-grow" v-click-outside="closeReqDropdown">
                        <!-- <label class="block text-sm font-medium text-gray-700">Requested By</label> -->
                        <div class="relative">
                          <input type="text" @focus="onRequesterFocus"
                            @keydown.down.prevent="onKeyDown('requester', 'down')"
                            @keydown.up.prevent="onKeyDown('requester', 'up')"
                            @keydown.enter.prevent="onEnter('requester', confirmRequesterChange, null, false)"
                            class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 dark:placeholder-gray-100"
                            placeholder="Select Requester" v-model="currentTicket.requester" />
                          <button v-if="currentTicket.requester != '' && currentTicket.contact_id != ''"
                            @click="clearReq"
                            class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500">
                            <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                              <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" />
                              <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                                stroke-linejoin="round" />
                            </svg>
                          </button>
                        </div>
                        <ul v-if="dropdownState.requester.visible"
                          class="absolute z-10 bg-white mt-1 rounded-md shadow-lg w-100 dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 scrollable-requester-list">
                          <li v-for="(req, index) in filteredRequesters" :key="req.id"
                            @click="confirmRequesterChange(req, false)"
                            :ref="el => setDropdownItemRef(el, index, 'requester')" :class="[
                              'cursor-pointer p-3',
                              dropdownState.requester.index === index ? 'bg-blue-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                            ]">
                            {{ req.name }}<span v-if="req.email"> - {{ req.email }}</span><span v-if="req.title"> ({{ req.title }})</span> ({{ req.user_type }})
                          </li>
                        </ul>
                      </div>
                      <div class="shrink-0">
                        <button @click="showAddUserModal = true"
                          class="mt-2 bg-gray-400 p-1 border rounded text-xs leading-none dark:text-gray-100 dark:border-gray-900 dark:bg-gray-900">
                          <!-- Plus icon -->
                          <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24"
                            stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <RequesterConfirmationModal v-if="showReqConfirmationModal" @confirm="handleReqConfirmation(true)"
                      @cancel="handleReqConfirmation(false)" />
                    <div>
                      <!-- <label class="block text-sm font-medium text-gray-700">Requested By</label> -->
                      <select
                        class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900"
                        v-model="currentTicket.clientLocation">
                        <option selected>Set Location</option>
                        <option v-for="address in currentTicket.additionalAddresses" :key="address.id"
                          :value="address.id">
                          {{ address.name ? address.name : address.address }}
                        </option>
                      </select>
                    </div>
                    <div v-click-outside="closeIntReqDropdown" v-if="!isCoManagedTech">
                      <div class="relative">
                        <input type="text" @focus="dropdownState.internalRequester.visible = true"
                          @keydown.down.prevent="onKeyDown('internalRequester', 'down')"
                          @keydown.up.prevent="onKeyDown('internalRequester', 'up')"
                          @keydown.enter.prevent="onEnter('internalRequester', confirmRequesterChange, null, true)"
                          class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 dark:placeholder-gray-100"
                          placeholder="Select Internal Requester" v-model="currentTicket.requesterInt" />
                        <button v-if="currentTicket.requesterInt != '' && currentTicket.contact_id != ''"
                          @click="clearIntReq"
                          class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500">
                          <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                              stroke-linejoin="round" />
                            <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                              stroke-linejoin="round" />
                          </svg>
                        </button>
                      </div>
                      <ul v-if="dropdownState.internalRequester.visible"
                        class="absolute z-10 bg-white mt-1 rounded-md shadow-lg w-100 dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 scrollable-requester-list">
                        <li v-for="(req, index) in filteredIntRequesters"
                          :ref="el => setDropdownItemRef(el, index, 'internalRequester')" :key="req.id"
                          @click="confirmRequesterChange(req, true)" :class="[
                            'cursor-pointer p-3',
                            dropdownState.internalRequester.index === index ? 'bg-blue-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                          ]">
                          {{ req.name }}<span v-if="req.email"> - {{ req.email }}</span>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
                <div class="md:col-span-2 mt-2">
                  <div class="grid grid-cols-1 md:grid-cols-2 gap-1">
                    <div>
                      <div class="flex">
                        <label class="block font-medium text-gray-700 text-xs dark:text-gray-100 mt-2 mr-2">Add a
                          Watcher?</label>
                        <button @click="toggleWatcher"
                          class="mt-1 bg-gray-400 p-1 border rounded text-xs leading-none dark:text-gray-100 dark:border-gray-900 dark:bg-gray-900 transition-transform duration-300"
                          :class="{ 'transform rotate-180': showWatcher }">
                          <svg
                            :class="{ 'fill-current text-gray-100': !showWatcher, 'fill-current text-blue-500': showWatcher }"
                            xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" class="h-4 w-4">
                            <path
                              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                          </svg>
                        </button>
                      </div>
                      <transition name="slide-fade">
                        <div v-click-outside="closeWatcherDropdown">
                          <input v-if="showWatcher" type="email" @focus="dropdownState.watcher.visible = true"
                            @keydown.down.prevent="onKeyDown('watcher', 'down')"
                            @keydown.up.prevent="onKeyDown('watcher', 'up')"
                            @keydown.enter.prevent="onEnter('watcher', addWatcher)"
                            class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 dark:placeholder-gray-100 transition-all duration-300 ease-in-out"
                            placeholder="Enter Requester Email if not in System" v-model="watchersInput" />
                          <ul v-if="dropdownState.watcher.visible"
                            class="absolute z-10 bg-white mt-1 rounded-md shadow-lg w-100 dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 scrollable-requester-list">
                            <li v-for="(watcher, index) in filteredWatchers" :key="watcher.id"
                              @click="addWatcher(watcher)" :ref="el => setDropdownItemRef(el, index, 'watcher')" :class="[
                                'cursor-pointer p-3',
                                dropdownState.watcher.index === index ? 'bg-blue-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                              ]">
                              {{ watcher.name }}
                            </li>
                          </ul>
                          <div class="mt-1 grid grid-cols-1 sm:grid-cols-1 md:grid-cols-1 gap-1">
                            <div v-for="watcher in currentTicket.watchers.filter(w => w.visible)" :key="watcher.id"
                              class="flex justify-between items-center border border-gray-200 rounded-full p-1 text-xs dark:border-gray-900 dark:bg-gray-400">
                              {{ watcher.name }}
                              <button @click="removeWatcher(watcher)"
                                class="ml-2 bg-red-600 hover:bg-red-700 text-white p-1 rounded-full text-xs">
                                &times;
                              </button>
                            </div>
                          </div>
                        </div>
                      </transition>
                    </div>
                    <!-- <div>
                      <div class="flex">
                        <label class="block font-medium text-gray-700 text-xs dark:text-gray-100 mt-2 mr-2">Add a
                          Assignee?</label>
                        <button @click="toggleRequesterAssignee"
                          class="mt-1 bg-gray-400 p-1 border rounded text-xs leading-none dark:text-gray-100 dark:border-gray-900 dark:bg-gray-900 transition-transform duration-300"
                          :class="{ 'transform rotate-180': showRequestAssignee }">
                          <svg
                            :class="{ 'fill-current text-gray-100': !showRequestAssignee, 'fill-current text-blue-500': showRequestAssignee }"
                            xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" class="h-4 w-4">
                            <path
                              d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" />
                          </svg>
                        </button>
                      </div>
                      <transition name="slide-fade">
                        <div v-click-outside="closeReqAssigneeDropdown">
                          <input v-if="showRequestAssignee" type="email"
                            class="mt-1 block w-full rounded-md border border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 dark:placeholder-gray-100 transition-all duration-300 ease-in-out"
                            placeholder="Enter Requester Email if not in System" @focus="toggleRequestAssigneeDropdown"
                            v-model="reqAssigneesInput"/>
                          <ul v-if="showRequestAssigneeDropdown"
                            class="absolute z-10 bg-white mt-1 rounded-md shadow-lg w-100 dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 scrollable-requester-list">
                            <li v-for="assignee in filteredRequestAssignees" :key="assignee.id"
                              @click="addRequestAssignee(assignee)"
                              class="cursor-pointer p-3 hover:bg-gray-100 dark:hover:bg-gray-800">
                              {{ assignee.name }}
                            </li>
                          </ul>
                          <div class="mt-1 grid grid-cols-1 sm:grid-cols-1 md:grid-cols-1 gap-1">
                            <div v-for="assignee in currentTicket.reqAssignees" :key="assignee.id"
                              class="flex justify-between items-center border border-gray-200 rounded-full p-1 text-xs dark:border-gray-900 dark:bg-gray-400">
                              {{ assignee.name }}
                              <button @click="removeRequestAssignee(assignee)" class="ml-2 bg-red-600 hover:bg-red-700 text-white p-1 rounded-full text-xs">
                                &times;
                              </button>
                            </div>
                          </div>
                        </div>
                      </transition>
                    </div> -->
                  </div>
                </div>
              </div>
            </div>
            <AddRequesterModal :isVisible="showAddUserModal" @close="showAddUserModal = false"
              @complete="handleAddUserCompletion" />
            <div class="flex space-x-2">
              <div class="mt-2 flex-grow">
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Email</label>
                <input type="email"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 dark:placeholder-gray-100"
                  placeholder="Email" v-model="currentTicket.email" @focus="handleFocus('email')"
                  @change="handleBlur('email')" />
                <p v-if="notifyBlocked" class="mt-1 text-xs text-gray-500 dark:text-gray-300">
                  Requester isn't emailed for private or internal tickets.
                </p>
              </div>
              <div class="shrink-0 mt-8">
                <input type="checkbox" id="notifyCheckbox"
                  class="form-checkbox h-4 w-4 text-blue-600 dark:text-gray-900 rounded"
                  v-model="notifyCheckboxModel" :disabled="notifyBlocked"
                  :class="{ 'opacity-50 cursor-not-allowed': notifyBlocked }" />
                <label for="notifyCheckbox" class=" ml-1 text-xs font-medium text-gray-700 dark:text-gray-100">
                  Notify
                </label>
              </div>
            </div>
            <div class="flex space-x-2">
              <div class="mt-2 flex-grow">
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Phone Number</label>
                <input type="email"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 dark:placeholder-gray-100"
                  placeholder="xxxxxxxxxx" v-model="currentTicket.alternatephone" @focus="handleFocus('alternatephone')"
                  @change="handleBlur('alternatephone')" />
              </div>
              <div class="shrink-0 mt-8">
                <div class="flex flex-col space-y-2">
                  <div class="flex items-center">
                    <input type="checkbox" id="publishCheckbox"
                      class="form-checkbox h-4 w-4 text-blue-600 dark:text-gray-900 rounded"
                      v-model="unPublishedCheckboxModel" />
                    <label for="publishCheckbox" class=" ml-1 text-xs font-medium text-gray-700 dark:text-gray-100">
                      DO NOT Publish
                    </label>
                  </div>
                  <div class="flex items-center">
                    <input type="checkbox" id="internalCheckbox"
                      class="form-checkbox h-4 w-4 text-blue-600 dark:text-gray-900 rounded"
                      v-model="internalCheckboxModel" />
                    <label for="internalCheckbox" class="ml-1 text-xs font-medium text-gray-700 dark:text-gray-100">
                      Internal
                    </label>
                  </div>
                </div>
              </div>
            </div>

            <ConfirmationModal :isVisible="showConfirmationModal" :field="fieldToConfirm" :value="valueToConfirm"
              @confirm="handleUpdateUserConfirm" @cancel="handleUpdateUserCancel" />

            <div class="mt-2">
              <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Source</label>
              <select
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:text-gray-100 dark:bg-gray-400 dark:border-gray-800"
                v-model="currentTicket.source">
                <!-- Loop through ticket sources and create an option for each source -->
                <option v-for="source in ticketSources" :value="source.name" :key="source.name">
                  {{ source.name }}
                </option>
              </select>
            </div>
            <div class="flex space-x-2">
              <div class="mt-2 flex-grow" v-click-outside="closeAssignDropdown">
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Assign To</label>
                <div class="relative">
                  <input type="text" @focus="dropdownState.tech.visible = true"
                    @keydown.down.prevent="onKeyDown('tech', 'down')" @keydown.up.prevent="onKeyDown('tech', 'up')"
                    @keydown.enter.prevent="onEnter('tech', setAssignee)"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 dark:placeholder-gray-100"
                    placeholder="Assign To" v-model="currentTicket.assign" />
                  <button v-if="currentTicket.assign != '' && currentTicket.tech_id != ''" @click="clearAssignee"
                    class="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-red-500">
                    <svg class="h-5 w-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round" />
                      <path d="M6 6L18 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                        stroke-linejoin="round" />
                    </svg>
                  </button>
                </div>
                <ul v-if="dropdownState.tech.visible"
                  class="absolute z-10 bg-white mt-1 rounded-md shadow-lg w-100 dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 scrollable-requester-list">
                  <li v-for="(assignee, index) in filteredAssignees" :key="assignee.id" @click="setAssignee(assignee)"
                    :ref="el => setDropdownItemRef(el, index, 'tech')" :class="[
                      'cursor-pointer p-3',
                      dropdownState.tech.index === index ? 'bg-blue-100 dark:bg-gray-700' : 'hover:bg-gray-100 dark:hover:bg-gray-800'
                    ]">
                    {{ assignee.name }}
                  </li>
                </ul>
              </div>
              <div class="shrink-0 mt-8">
                <input type="checkbox" id="urgentCheckbox"
                  class="form-checkbox h-4 w-4 text-blue-600 dark:text-gray-900 rounded"
                  v-model="urgencyCheckboxModel" />
                <label for="urgentCheckbox" class=" ml-1 text-xs font-medium text-gray-700 dark:text-gray-100">
                  Urgent
                </label>
              </div>
            </div>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-1">
              <div class="mt-2">
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Estimated Hours</label>
                <input type="number"
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 dark:placeholder-gray-100"
                  placeholder="Extimated Hours" v-model="currentTicket.esttime" />
              </div>
              <div class="mt-2">
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Select Estimated hours</label>
                <select
                  class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100"
                  v-model="currentTicket.esttime">
                  <option value="0.1">0.1</option>
                  <option value="0.25">0.25</option>
                  <option value="0.50">0.50</option>
                  <option value="0.75">0.75</option>
                  <option value="1.00">1.00</option>
                  <option value="1.50">1.50</option>
                  <option value="2.00">2.00</option>
                  <option value="3.00">3.00</option>
                  <option value="4.00">4.00</option>
                </select>
              </div>
            </div>

            <div class="mt-2">
              <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Deadline</label>
              <input type="datetime-local"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 dark:placeholder-gray-100"
                v-model="currentTicket.deadline" />
            </div>
            <div class="md:col-span-2">
              <div class="flex items-center gap-2">
                <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">
                  When would you like this handled?
                </label>

                <div class="relative group cursor-pointer">
                  <svg class="w-4 h-4 text-blue-500" xmlns="http://www.w3.org/2000/svg" fill="currentColor"
                    viewBox="0 0 24 24">
                    <path fill-rule="evenodd"
                      d="M2 12C2 6.477 6.477 2 12 2s10 4.477 10 10-4.477 10-10 10S2 17.523 2 12Zm9.408-5.5a1 1 0 1 0 0 2h.01a1 1 0 1 0 0-2h-.01ZM10 10a1 1 0 1 0 0 2h1v3h-1a1 1 0 1 0 0 2h4a1 1 0 1 0 0-2h-1v-4a1 1 0 0 0-1-1h-2Z"
                      clip-rule="evenodd" />
                  </svg>
                  <div
                    class="absolute left-5 top-0 z-20 hidden group-hover:block bg-gray-800 text-white text-xs rounded-md shadow-md px-3 py-2 w-64 whitespace-normal">
                    This sets the user requested date/time and the requested timeframe.
                  </div>
                </div>
              </div>

              <select v-model="currentTicket.userrequesttimeframe_id"
                @change="changeUserRequestTimeframe(currentTicket.userrequesttimeframe_id)"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100">
                <option value="0" disabled>Select a Quick Day</option>
                <option v-for="type in timeFrames" :key="type.id" :value="type.id">
                  {{ type.label }}
                </option>
              </select>
            </div>
            <div class="md:col-span-2 mt-2">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-1">
                <div>
                  <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Promised</label>
                  <input type="datetime-local"
                    class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 dark:placeholder-gray-100"
                    v-model="currentTicket.promised" />
                </div>
                <div class="grid grid-cols-1 md:grid-cols-2 gap-1">
                  <div>
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Day</label>
                    <select
                      class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100"
                      v-model="selectedDay">
                      <option value="-1" disabled>Select a Quick Day</option>
                      <option value="0">Today</option>
                      <option value="1">Tomorrow</option>
                      <option value="2">2 Days</option>
                      <option value="3">3 Days</option>
                      <option value="4">4 Days</option>
                      <option value="5">5 Days</option>
                      <option value="6">6 Days</option>
                      <option value="7">1 Week</option>
                      <option value="14">2 Weeks</option>
                      <option value="21">3 Weeks</option>
                      <option value="30">1 Month</option>
                      <option value="60">2 Months</option>
                      <option value="90">3 Months</option>
                    </select>
                  </div>
                  <div>
                    <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Time</label>
                    <select
                      class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100"
                      v-model="selectedTime">
                      <option value="-1" disabled>Select a Quick Time</option>
                      <option value="07:00">7:00</option>
                      <option value="08:00">8:00</option>
                      <option value="09:00">9:00</option>
                      <option value="10:00">10:00</option>
                      <option value="11:00">11:00</option>
                      <option value="12:00">12:00</option>
                      <option value="13:00">1:00</option>
                      <option value="14:00">2:00</option>
                      <option value="15:00">3:00</option>
                      <option value="16:00">4:00</option>
                      <option value="17:00">5:00</option>
                      <option value="18:00">6:00</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="currentTicket.attachTo && currentTicket.attachedRelatedTicket"
              class="md:col-span-2 mt-3 mb-2 p-3 rounded-md border-2 border-yellow-500 bg-yellow-50 dark:bg-yellow-900/30 dark:border-yellow-400">
              <div class="flex items-start justify-between gap-3">
                <div class="text-xs text-yellow-900 dark:text-yellow-100">
                  <div class="font-semibold mb-1">
                    ⚠ Attached Related Ticket Selected
                  </div>

                  <div>
                    #{{ currentTicket.attachedRelatedTicket.id }}
                    {{ currentTicket.attachedRelatedTicket.tickettype }}
                    —
                    {{ currentTicket.attachedRelatedTicket.firstname }}
                    {{ currentTicket.attachedRelatedTicket.lastname }}
                  </div>

                  <div class="italic">
                    {{ currentTicket.attachedRelatedTicket.requester }}
                  </div>

                  <div class="mt-1">
                    <template v-if="currentTicket.attachedRelatedTicket.description?.includes(':')">
                      {{ currentTicket.attachedRelatedTicket.tickettype }}{{
                        currentTicket.attachedRelatedTicket.description }}
                    </template>
                    <template v-else>
                      {{ currentTicket.attachedRelatedTicket.tickettype }}: {{
                        currentTicket.attachedRelatedTicket.description }}
                    </template>
                  </div>
                </div>

                <button type="button" @click="
                  currentTicket.attachTo = 0;
                currentTicket.attachedRelatedTicket = null;
                " class="text-xs px-2 py-1 rounded bg-red-500 text-white hover:bg-red-600">
                  Remove
                </button>
              </div>
            </div>
            <div class="text-right md:col-span-2 mt-2">
              <button
                class="inline-flex mr-4 items-center px-4 py-2 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-yellow-600 hover:bg-yellow-700 dark:bg-yellow-400 dark:hover:bg-yellow-700"
                @click="showModal = true">
                Open related tickets
              </button>
              <button type="submit"
                class="inline-flex items-center mr-4 px-4 py-2 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 dark:bg-blue-400 dark:hover:bg-blue-700"
                @click="delegateTicket(currentTicket, false)">
                Delegate
              </button>
              <button type="submit" v-if="!isCoManagedTech"
                class="inline-flex mr-4 items-center px-4 py-2 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 dark:bg-green-300 dark:hover:bg-green-700"
                @click="delegateTicket(currentTicket, true)">
                Start
              </button>
              <button type="submit" v-if="!isCoManagedTech"
                class="inline-flex items-center px-4 py-2 border border-transparent text-xs font-medium rounded-md shadow-sm text-white bg-red-500 hover:bg-red-700 dark:bg-red-300 dark:hover:bg-red-600"
                @click="deleteTicket(currentTicket.id)">
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Second Column: Notes and Related Tickets -->
      <div class="flex-1 border rounded-2xl px-6 py-6 bg-white rounded shadow dark:bg-gray-600 dark:border-gray-600"
        v-if="currentTicket">
        <div class="flex-1 border rounded-2xl px-6 py-6 bg-white rounded dark:bg-gray-600 dark:border-gray-600">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-2" v-if="currentTicket">
            <!-- <div class="mt-2">
              <label
                class="block text-xs font-medium text-gray-700 dark:text-gray-100"
                >Project Related</label
              >
              <select
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:text-gray-100 dark:bg-gray-400 dark:border-gray-800"
                v-model="currentTicket.project_id"
              >
                <option value="" selected>Please Select</option>
                <option
                  v-for="project in currentTicket.projects"
                  :key="project.id"
                  :value="project.id"
                  :selected="currentTicket.project_id == project.id"
                >
                  {{ project.created }} / {{ project.name }}
                </option>
              </select>
            </div> -->
            <!-- <div class="mt-2">
              <label
                class="block text-xs font-medium text-gray-700 dark:text-gray-100"
                >Redmine</label
              >
              <input
                type="number"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:text-gray-100 dark:bg-gray-400 dark:border-gray-800 dark:placeholder-gray-100"
                v-model="currentTicket.redmineissueid"
                placeholder="Redmine ID"
              />
            </div> -->

            <!-- <div class="md:col-span-2 mt-2">
              <label
                class="block text-xs font-medium text-gray-700 dark:text-gray-100"
                >PC/Server</label
              >
              <select
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:text-gray-100 dark:bg-gray-400 dark:border-gray-800"
                v-model="currentTicket.pcid"
              >
                <option selected>Select PC/Server</option>
                <option>2024-11-26</option>
              </select>
            </div> -->

            <div class="relative md:col-span-2 mt-2">
              <label for="requesterNotes"
                class="block text-xs font-medium text-gray-700 dark:text-gray-100">Description</label>
              <textarea id="requesterNotes"
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 dark:placeholder-gray-100"
                rows="2" v-model="currentTicket.description" style="padding-bottom: 2rem"
                @change="changedNotes('description')"></textarea>
              <button @click="activateTextArea('requester')"
                class="absolute right-2 bg-gray-400 bottom-2 p-1 border rounded text-xs leading-none dark:text-gray-100 dark:border-gray-900 dark:bg-gray-900"
                style="margin-right: 1rem">
                [ ]
              </button>
            </div>
            <!-- Large Textarea Modal -->
            <LargeTextareaModal :isVisible="showTextArea" :initialText="getTextareaInitialText()"
              :activeTextarea="activeTextarea" @close="showTextArea = false" @complete="handleTextCompletion" />

            <div class="relative md:col-span-2 mt-2">
              <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Notes/Direction (Public)</label>
              <textarea
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 dark:placeholder-gray-100"
                rows="3" v-model="currentTicket.delegatornotes" @change="changedNotes('delegatornotes')"></textarea>
              <button @click="activateTextArea('aimNotesPublic')"
                class="absolute right-2 bg-gray-400 bottom-2 p-1 border rounded text-xs leading-none dark:text-gray-100 dark:border-gray-900 dark:bg-gray-900"
                style="margin-right: 1rem">
                [ ]
              </button>
            </div>

            <div class="relative md:col-span-2">
              <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Private Info</label>
              <textarea
                class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:bg-gray-400 dark:border-gray-900 dark:text-gray-100 dark:placeholder-gray-100"
                rows="3" v-model="currentTicket.privateinfo" placeholder="No private info to share"
                @change="changedNotes('privateinfo')"></textarea>
              <button @click="activateTextArea('privateNotes')"
                class="absolute right-2 bg-gray-400 bottom-2 p-1 border rounded text-xs leading-none dark:text-gray-100 dark:border-gray-900 dark:bg-gray-900"
                style="margin-right: 1rem">
                [ ]
              </button>
            </div>

            <div class="md:col-span-2 flex items-center">
              <label class="block text-xs font-medium text-gray-700 dark:text-gray-100">Attach Document:</label>
              <input type="file" @change="handleFileUpload" ref="fileInput" multiple
                class="ml-2 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:text-gray-100" />
              <!-- <span class="dark:text-gray-100" v-if="uploadedFile">{{
                uploadedFile.name
              }}</span> -->
            </div>
            <!-- Display of uploaded documents -->
            <div class="md:col-span-2 mt-4">
              <div
                class="p-4 bg-white shadow-md rounded-md border-2 border-blue-900 relative overflow-x-auto dark:text-gray-100 dark:bg-gray-400 dark:border-gray-800">
                <div
                  class="absolute top-0 left-0 right-0 bg-blue-900 px-3 py-1 flex items-center dark:bg-gray-800 w-full">
                  <div class="flex items-center space-x-2">
                    <h4 class="text-white font-bold text-xs">
                      Attached Documents
                    </h4>
                  </div>
                </div>
                <div>
                  <table class="min-w-full divide-y divide-gray-200 mt-4">
                    <thead>
                      <tr>
                        <th class="px-1 py-1 text-left text-xs font-medium text-gray-900 uppercase">
                          Name
                        </th>
                        <th class="px-1 py-1 text-left text-xs font-medium text-gray-900 uppercase">Preview</th>
                        <th class="px-1 py-1 text-left text-xs font-medium text-gray-900 uppercase">
                          Type
                        </th>
                        <th class="px-1 py-1 text-left text-xs font-medium text-gray-900 uppercase">
                          Delete
                        </th>
                        <th class="px-1 py-1 text-left text-xs font-medium text-gray-900 uppercase">
                          Black list
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-if="currentTicket.documents && currentTicket.documents.length > 0"
                        v-for="doc in currentTicket.documents" :key="doc.id"
                        class="bg-white dark:text-gray-100 dark:bg-gray-400 dark:border-gray-800"
                        :class="doc.blacklisted ? 'opacity-50 cursor-not-allowed' : ''">
                        <td class="px-1 py-3 whitespace-nowrap text-xs text-gray-900">
                          <a :href="doc.download_url" :download="doc.doc_name" target="_blank" class="hover:underline">
                            {{ doc.doc_name }}
                          </a>
                        </td>
                        <td class="px-1 py-3 whitespace-nowrap text-xs text-gray-900">
                          <a v-if="doc.is_image" :href="doc.view_url" target="_blank" rel="noopener">
                            <img :src="doc.view_url" alt="" class="h-10 w-10 object-cover rounded border" />
                          </a>
                        </td>
                        <td class="px-1 py-3 whitespace-nowrap text-xs text-gray-900">
                          {{ doc.mime }}
                        </td>
                        <td class="px-1 py-3 whitespace-nowrap text-xs font-medium">
                          <a href="#" @click="deleteDocument(doc)" class="text-red-600 hover:text-red-900"
                            :class="{ 'pointer-events-none opacity-50': doc.blacklisted }">Delete</a>
                        </td>
                        <td class="px-1 py-3 whitespace-nowrap text-xs font-medium">
                          <a v-if="!doc.isNew" href="#" @click.prevent="blacklistDocument(doc)"
                            class="text-red-600 hover:text-red-900"
                            :class="{ 'pointer-events-none opacity-50': doc.blacklisted }"
                            title="Remove this file and skip identical attachments on future emails">Black List</a>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  <div class="mt-2 text-xs">
                    <a href="#" @click.prevent="toggleBlacklistPanel" class="text-blue-700 hover:underline dark:text-blue-200">
                      {{ showBlacklist ? 'Hide' : 'Show' }} blacklisted files
                    </a>
                    <div v-if="showBlacklist" class="mt-2">
                      <p v-if="blacklistLoading" class="text-gray-500 dark:text-gray-100">Loading…</p>
                      <p v-else-if="blacklistEntries.length === 0" class="text-gray-500 dark:text-gray-100">
                        No files are blacklisted.
                      </p>
                      <table v-else class="min-w-full divide-y divide-gray-200">
                        <thead>
                          <tr>
                            <th class="px-1 py-1 text-left text-xs font-medium text-gray-900 uppercase">Name</th>
                            <th class="px-1 py-1 text-left text-xs font-medium text-gray-900 uppercase">Type</th>
                            <th class="px-1 py-1 text-left text-xs font-medium text-gray-900 uppercase">Added</th>
                            <th class="px-1 py-1"></th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr v-for="entry in blacklistEntries" :key="entry.id">
                            <td class="px-1 py-2 whitespace-nowrap text-xs text-gray-900">{{ entry.doc_name }}</td>
                            <td class="px-1 py-2 whitespace-nowrap text-xs text-gray-900">{{ entry.mime }}</td>
                            <td class="px-1 py-2 whitespace-nowrap text-xs text-gray-900">
                              {{ entry.created_at ? new Date(entry.created_at).toLocaleDateString() : '' }}
                            </td>
                            <td class="px-1 py-2 whitespace-nowrap text-xs font-medium">
                              <a href="#" @click.prevent="unblacklist(entry)" class="text-blue-700 hover:underline">Remove</a>
                            </td>
                          </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Active Jobs Table Column -->
      <div class="flex-1 border rounded-2xl px-3 py-6 bg-white rounded shadow dark:bg-gray-600 dark:border-gray-600"
        v-if="userroles.some(r => [10, 20, 30, 40].includes(r))">
        <div class="mb-1" v-if="clientNotes.warning && totalTickets.length > 0">
          <div class="p-3 bg-red-100 border-2 border-red-500 rounded shadow-md dark:bg-red-800 dark:border-red-400">
            <h3 class="text-xs font-medium text-gray-900 dark:text-gray-100">
              ⚠ Warnings / Account Notices
            </h3>

            <textarea
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:text-gray-100 dark:bg-gray-400 dark:border-gray-800 dark:placeholder-gray-100"
              rows="3" placeholder="Enter client notes here..." v-model="clientNotes.warning"></textarea>
          </div>
        </div>
        <div class="mb-1" v-if="clientNotes.notes && totalTickets.length > 0">
          <div class="p-3 bg-red-100 border-2 border-red-500 rounded shadow-md dark:bg-red-800 dark:border-red-400">
            <h3 class="text-xs font-medium text-gray-900 dark:text-gray-100">
              Client Notes:
            </h3>
            <textarea
              class="mt-1 block w-full rounded-md border-gray-300 shadow-sm text-xs dark:text-gray-100 dark:bg-gray-400 dark:border-gray-800 dark:placeholder-gray-100"
              rows="3" placeholder="Enter client notes here..." v-model="clientNotes.notes"></textarea>
          </div>
        </div>
        <label class="block text-l font-medium text-gray-700 text-center dark:text-gray-100 mt-2">On-call: {{
          oncall.firstname }} {{ oncall.lastname }}</label>
        <hr class="mt-4 border-t-2 border-gray-300" />
        <label class="block text-m font-medium text-gray-700 text-center mt-4 dark:text-gray-100">Active Jobs</label>
        <table class="divide-y divide-gray-200 mt-4 rounded-lg overflow-hidden"
          :class="{ 'w-full': totalTickets.length == 0 }">
          <thead class="bg-gray-400 dark:bg-gray-800 dark:border-gray-600">
            <tr>
              <th scope="col" class="px-1 py-3 text-xs font-medium text-gray-50 uppercase tracking-wider"
                :class="{ 'text-left px-6': totalTickets.length == 0 }">
                Tech
              </th>
              <th scope="col" class="px-1 py-3 text-xs font-medium text-gray-50 uppercase tracking-wider"
                :class="{ 'text-left px-6': totalTickets.length == 0 }">
                Remaining hours
              </th>
              <th scope="col" class="px-1 py-3 text-xs font-medium text-gray-50 uppercase tracking-wider"
                :class="{ 'text-left px-6': totalTickets.length == 0 }">
                Started
              </th>
              <th scope="col" class="px-1 py-3 text-xs font-medium text-gray-50 uppercase tracking-wider"
                :class="{ 'text-left px-6': totalTickets.length == 0 }">
                Time
              </th>
              <th scope="col" class="px-1 py-3 text-xs font-medium text-gray-50 uppercase tracking-wider"
                :class="{ 'text-left px-6': totalTickets.length == 0 }">
                Client
              </th>
              <!-- <th
                scope="col"
                class="px-1 py-3 text-xs font-medium text-gray-50 uppercase tracking-wider"
                :class="{ 'text-left px-6': totalTickets.length == 0 }"
              >
                Ticket/RM
              </th> -->
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <template v-for="job in activeJobs" :key="job.jobid">
              <tr>
                <td
                  class="px-2 py-4 whitespace-nowrap text-xs font-medium text-blue-600 underline cursor-pointer hover:text-blue-800"
                  :class="[
                    { 'px-6': totalTickets.length == 0 },
                    jobTypeClass(job.jobtype, job.background)
                  ]" @click="handleEditJob(job.uid)">
                  {{ job.name }} ({{ job.nonprojectticketcount }})
                </td>
                <td class="px-2 py-4 whitespace-nowrap text-xs font-medium" :class="({ 'px-6': totalTickets.length == 0 },
                  jobTypeClass(job.jobtype, job.background))
                  ">
                  {{ (8 - job.totalWorkedTime).toFixed(2) }}
                </td>
                <td class="px-2 py-4 whitespace-nowrap text-xs font-medium" :class="({ 'px-6': totalTickets.length == 0 },
                  jobTypeClass(job.jobtype, job.background))
                  ">
                  {{ job.stime }}
                </td>
                <td class="px-2 py-4 whitespace-nowrap text-xs font-medium" :class="({ 'px-6': totalTickets.length == 0 },
                  jobTypeClass(job.jobtype, job.background))
                  ">
                  {{ parseFloat(job.howlong || 0).toFixed(2) }}
                </td>
                <td class="px-2 py-4 whitespace-nowrap text-xs font-medium" :class="({ 'px-6': totalTickets.length == 0 },
                  jobTypeClass(job.jobtype, job.background))
                  ">
                  {{ job.shortname }}
                </td>
                <!-- <td
                  class="px-2 py-4 whitespace-nowrap text-xs font-medium"
                  :class="
                    ({ 'px-6': totalTickets.length == 0 },
                    jobTypeClass(job.jobtype, job.background))
                  "
                >
                  <a
                    v-if="job.ticket_id != 0"
                    :href="`https://atms.aimitservices.com/viewTickets.php?searchtext=${job.ticket_id}`"
                    target="_blank"
                    style="
                      color: #0000ee;
                      text-decoration: underline;
                      margin-right: 10px;
                    "
                    >#{{ job.ticket_id }}</a
                  >
                  <a
                    v-if="job.redmine != 0"
                    :href="`https://redmine.aimitservices.com/issues/${job.redmine}`"
                    target="_blank"
                    style="color: #0000ee; text-decoration: underline"
                    >RM{{ job.redmine }}</a
                  >
                </td> -->
              </tr>
            </template>
            <tr
              class="bg-gray-400 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 font-medium text-gray-50 uppercase tracking-wider text-l">
              <td colspan="6" align="center">
                <br />
                <h2>Not Clocked In</h2>
                <br />
              </td>
            </tr>
            <template v-for="job in lastJobs" :key="job.jobid">
              <tr>
                <td
                  class="px-2 py-4 whitespace-nowrap text-xs font-medium text-blue-600 underline cursor-pointer hover:text-blue-800"
                  :class="[
                    { 'px-6': totalTickets.length == 0 },
                    jobTypeClass(job.jobtype, job.background)
                  ]" @click="handleEditJob(job.uid)">
                  {{ job.name }} ({{ job.nonprojectticketcount }})
                </td>
                <td class="px-2 py-4 whitespace-nowrap text-xs font-medium" :class="({ 'px-6': totalTickets.length == 0 },
                  jobTypeClass(job.jobtype, job.background))
                  ">
                  {{ job.project }}
                </td>
                <td class="px-2 py-4 whitespace-nowrap text-xs font-medium" :class="({ 'px-6': totalTickets.length == 0 },
                  jobTypeClass(job.jobtype, job.background))
                  ">
                  {{ job.stime }}
                </td>
                <td class="px-2 py-4 whitespace-nowrap text-xs font-medium" :class="({ 'px-6': totalTickets.length == 0 },
                  jobTypeClass(job.jobtype, job.background))
                  ">
                  {{ job.howlong }}
                </td>
                <td class="px-2 py-4 whitespace-nowrap text-xs font-medium" :class="({ 'px-6': totalTickets.length == 0 },
                  jobTypeClass(job.jobtype, job.background))
                  ">
                  {{ job.shortname }}
                </td>
                <!-- <td
                  class="px-2 py-4 whitespace-nowrap text-xs font-medium"
                  :class="
                    ({ 'px-6': totalTickets.length == 0 },
                    jobTypeClass(job.jobtype, job.background))
                  "
                >
                  <a
                    v-if="job.ticket_id != 0"
                    :href="`https://atms.aimitservices.com/viewTickets.php?searchtext=${job.ticket_id}`"
                    target="_blank"
                    style="
                      color: #0000ee;
                      text-decoration: underline;
                      margin-right: 10px;
                    "
                    >#{{ job.ticket_id }}</a
                  >
                  <a
                    v-if="job.redmine != 0"
                    :href="`https://redmine.aimitservices.com/issues/${job.redmine}`"
                    target="_blank"
                    style="color: #0000ee; text-decoration: underline"
                    >RM{{ job.redmine }}</a
                  >
                </td> -->
              </tr>
            </template>
          </tbody>
        </table>
      </div>
    </div>
    <!-- Modal -->
    <div v-if="showModal"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 overflow-y-auto h-full w-full flex justify-center items-center">
      <TicketsModal v-show="currentStep === 1" @close="handleClose" :clientId="currentTicket.company_id"
        :attachTo="currentTicket.attachTo || 0" :excludeTicketId="currentTicket.id"
        @handleRelatedTickets="handleRelatedTickets" />
    </div>
    <div v-if="showJobsModal"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 z-50 overflow-y-auto flex justify-center items-center">
      <div class="bg-white dark:bg-gray-700 rounded-lg shadow-lg w-full max-w-3xl p-4">
        <div class="flex justify-between items-center mb-4">
          <h2 class="text-sm font-bold text-gray-800 dark:text-gray-100">Job Activity on this Ticket</h2>
          <button class="text-gray-700 dark:text-gray-100 hover:text-red-500" @click="showJobsModal = false">
            ✕
          </button>
        </div>
        <div class="overflow-y-auto max-h-[70vh]">
          <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-600 text-xs">
            <thead class="bg-gray-100 dark:bg-gray-700">
              <tr>
                <th class="px-2 py-2 text-left text-gray-700 dark:text-gray-100">Job ID</th>
                <th class="px-2 py-2 text-left text-gray-700 dark:text-gray-100">Date</th>
                <th class="px-2 py-2 text-left text-gray-700 dark:text-gray-100">Author</th>
                <th class="px-2 py-2 text-left text-gray-700 dark:text-gray-100">Description</th>
                <th class="px-2 py-2 text-left text-gray-700 dark:text-gray-100">Total Time</th>
              </tr>
            </thead>
            <tbody class="bg-white dark:bg-gray-600 divide-y divide-gray-200 dark:divide-gray-600">
              <tr v-for="job in currentTicket.jobs" :key="job.id" class="border-b border-gray-200 dark:border-gray-600">
                <td class="px-2 py-2">{{ job.id }}</td>
                <td class="px-2 py-2">{{ job.jdate }}</td>
                <td class="px-2 py-2">{{ getAuthor(job) }}</td>
                <td class="px-2 py-2">
                  <div v-html="renderMarkdown(job.description, { compact: true })"></div>
                </td>
                <td class="px-2 py-2">{{ job.totaltime }}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { renderMarkdown } from '@/utils/markdown';
import { defineEmits, onMounted, onBeforeUnmount, ref, reactive, computed, watch, getCurrentInstance, nextTick } from "vue";
import TicketsModal from "@/views/modals/TicketsModal.vue";
import LargeTextareaModal from "@/views/modals/LargeTextareaModal.vue";
import AddRequesterModal from "@/views/modals/AddRequesterModal.vue";
import ConfirmationModal from "@/views/modals/ConfirmationModal.vue";
import RequesterConfirmationModal from "@/views/modals/RequesterConfirmationModal.vue";
import BroadcastComposerModal from "@/views/modals/BroadcastComposerModal.vue";
import BroadcastManagePanel from "@/views/components/BroadcastManagePanel.vue";
import axios from "axios";
import { searchRequesters } from "@/utils/requesterSearch";
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
const router = useRouter();
const showModal = ref(false);

// --- Broadcasts ---------------------------------------------------------
const showBroadcastComposer = ref(false);
const broadcastReloadSignal = ref(0);
const activeBroadcastCompanies = ref([]);
const composerEditBroadcast = ref(null);

// Broadcast controls are for managers (10/20) and internal techs (30).
const canManageBroadcasts = computed(() =>
  (userroles.value || []).some(r => [10, 20, 30].includes(Number(r))));

// The current ticket's client has a live broadcast → auto "possible duplicate" flag.
const currentTicketHasBroadcast = computed(() => {
  const cid = currentTicket.value && Number(currentTicket.value.company_id);
  return !!cid && activeBroadcastCompanies.value.includes(cid);
});

// Pre-fill the composer with the ticket the manager is looking at.
const composerPresetClient = computed(() => {
  const t = currentTicket.value;
  if (!t || !t.company_id) return null;
  return { id: t.company_id, companyname: t.client?.companyname || t.clientName || '' };
});
const composerPresetTicketId = computed(() => currentTicket.value?.id || null);

const openBroadcastComposer = () => { composerEditBroadcast.value = null; showBroadcastComposer.value = true; };
const onEditBroadcast = (b) => { composerEditBroadcast.value = b; showBroadcastComposer.value = true; };
const onBroadcastCreated = () => { broadcastReloadSignal.value++; };
const onActiveBroadcastCompanies = (ids) => { activeBroadcastCompanies.value = ids; };
// ------------------------------------------------------------------------

const showWatcher = ref(false);
const showRequestAssignee = ref(false);
const showConfirmationModal = ref(false);
const showReqConfirmationModal = ref(false);
const fieldToConfirm = ref('');
const valueToConfirm = ref('');
const currentPage = ref(1);
const currentStep = ref(1);
const selectedDay = ref('-1');
const selectedTime = ref('-1')
const ticketsPerPage = 1; // Or any number you like
const inputFocused = ref(false);
const watchersInput = ref('');
const reqAssigneesInput = ref('');
const store = useStore();
const leaveToday = ref([]);
const timeFrames = ref([]);
const leaveFuture = ref([]);
const hoveredLeaveIndex = ref(null);
const hoveredLeaveType = ref('');

const getCurrentLocalTime = () => {
  const now = new Date();
  return { days: 0, time: now.toTimeString().slice(0, 5) }; // HH:MM format of current time
};
const getNextBusinessDayOffset = () => {
  const today = new Date();
  const day = today.getDay();

  if (day === 5) return 3;
  if (day === 6) return 2;
  return 1;
};
const getEndOfWeekFriday = () => {
  const today = new Date();
  const currentDay = today.getDay();
  const daysUntilFriday = (5 - currentDay + 7) % 7; // Calculate days until next Friday
  return { days: daysUntilFriday, time: '17:00' };
};
const getEndOfNextWeekFriday = () => {
  const today = new Date();
  const currentDay = today.getDay();
  const daysUntilNextFriday = ((5 - currentDay + 7) % 7) + 7; // Calculate days until the Friday after next
  return { days: daysUntilNextFriday, time: '17:00' };
};
const timeframesMapping = ref({
  '1': getCurrentLocalTime(), // Immediately (current local time)
  '2': { days: 0, time: '12:00' }, // By noon today
  '3': { days: 0, time: '17:00' }, // By end of day
  '8': { days: getNextBusinessDayOffset(), time: '12:00' }, // Noon next business day
  '4': { days: getNextBusinessDayOffset(), time: '17:00' }, // End of next business day
  '5': getEndOfWeekFriday(), // By end of week (upcoming Friday 5 PM)
  '6': getEndOfNextWeekFriday(), // By end of next week (following Friday 5 PM)
  '7': { days: 0, time: '17:00' }, // No rush, whenever you can get to it
});
let actionItemsInterval = null;

const totalTickets = ref([]);
// console.log(showModal.value);
const activeJobs = ref([]);
const jobTypeClass = (type, background) => {
  if (background) {
    if (background == '33A2FF') {
      return 'd33A2FF';
    } else if (background == 'D3ACD4') {
      return 'd3acd4';
    }
    return background.toLowerCase().replace(/\s+/g, '-');
  }

  // Fallback to old jobtype mapping if background not set
  switch (type) {
    case "W":
      return "development";
    case "R":
      return "remote";
    case "L":
      return "lunch";
    case "B":
      return "bench";
    default:
      return "";
  }
};
let proxyInstance = ref();
watch(() => store.state.shouldReloadDelegatorTickets, (newVal) => {
  proxyInstance.$updateLoading(1);
  fetchTickets();
  proxyInstance.$updateLoading(-1);
});

const clients = ref([]);
const intRequesters = ref([]);
const ticketSources = ref([]);
const lastJobs = ref([]);
const userroles = ref([]);
const dupJobs = ref([]);
const employees = ref([]);
const oncall = ref([]);
const showJobsModal = ref(false);
const clientNotes = ref([]);
const dropdownState = reactive({
  client: { visible: false, index: -1 },
  requester: { visible: false, index: -1 },
  type: { visible: false, index: -1 },
  internalRequester: { index: -1, visible: false },
  watcher: { index: -1, visible: false },
  tech: { index: -1, visible: false }
});
const formatDateTimeLocal = (inputString) => {
  // Expected input format "YYYY-MM-DD HH:mm:ss"
  if (inputString != '0000-00-00 00:00:00') {
    const parts = inputString.split(/[- :]/);
    const formattedDate = new Date(parts[0], parts[1] - 1, parts[2], parts[3], parts[4], parts[5]);
    const date = formattedDate.toLocaleDateString('en-CA');

    // Time in HH:mm (local 24-hour)
    const time = formattedDate.toLocaleTimeString('en-CA', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false
    });

    return `${date}T${time}`;
  }
  return inputString;
};
const getUserRole = async () => {
  try {
    const roleResponse = await axios.get(`user/role/${localStorage.getItem('user_id')}`);
    userroles.value = roleResponse.data.roles;
  } catch (error) {
    console.error('Error fetching user role:', error);
  }
};
onMounted(async () => {
  const { proxy } = getCurrentInstance();
  proxyInstance = proxy;
  proxyInstance.$updateLoading(1);
  axios
    .get(`${axios.defaults.baseURL}clients`)
    .then((response) => {
      clients.value = response.data;
    })
    .catch((error) => {
      console.error("There was an error fetching clients:", error);
    });

  try {
    const [, , , requestersResponse, requestersIntResponse] = await Promise.all([
      getUserRole(),
      fetchActionItems(),
      fetchTickets(),
      axios.get(`${axios.defaults.baseURL}requesters`),
      axios.get(`${axios.defaults.baseURL}requesters/internal`),
    ]);
    requesters.value = requestersResponse.data;
    intRequesters.value = requestersIntResponse.data;
  } catch (error) {
    console.error("There was an error fetching data:", error);
  }
  // axios
  //   .get(`${axios.defaults.baseURL}user/active-users`)
  //   .then((response) => {
  //     employees.value = response.data;
  //   })
  //   .catch((error) => {
  //     console.error("There was an error fetching active users:", error);
  //   });
  axios.get(`${axios.defaults.baseURL}ticket/timeframes`)
    .then(response => {
      timeFrames.value = response.data;
    })
    .catch(error => {
      console.error(error);
    });
  axios
    .get(`${axios.defaults.baseURL}ticket/sources`)
    .then((response) => {
      ticketSources.value = response.data;
    })
    .catch((error) => {
      console.error("There was an error fetching sources:", error);
    });

  axios
    .get(`${axios.defaults.baseURL}ticket/oncall`)
    .then((response) => {
      oncall.value = response.data;
    })
    .catch((error) => {
      console.error("There was an error fetching on-call user:", error);
    });
  axios
    .get(`${axios.defaults.baseURL}job/current`)
    .then((response) => {
      dupJobs.value = response.data;

      // Separate jobs into two arrays based on stime condition
      activeJobs.value = dupJobs.value.filter((job) => job.stime != "Last Job");
      lastJobs.value = dupJobs.value.filter((job) => job.stime == "Last Job");

      // A tech can only have one running job, so the active list must have at
      // most one row per uid. Collapse duplicates defensively (mirrors the
      // dedup on lastJobs below) so a fan-out in the API response can never
      // render the same tech twice.
      activeJobs.value = activeJobs.value.filter(
        (value, index, self) =>
          index === self.findIndex((t) => t.uid === value.uid)
      );

      // Filter lastJob array to exclude names present in notLastJob array
      lastJobs.value = lastJobs.value.filter(
        (lastJob) =>
          !activeJobs.value.some((activeJob) => activeJob.uid === lastJob.uid)
      );

      lastJobs.value = lastJobs.value.filter(
        (value, index, self) =>
          index === self.findIndex((t) => t.uid === value.uid)
      );
      console.log(activeJobs.value);
    })
    .catch((error) => {
      console.error("There was an error fetching current jobs:", error);
    });

  proxyInstance.$updateLoading(-1);
  actionItemsInterval = setInterval(() => {
    fetchActionItems();
  }, 60000);
});
onBeforeUnmount(() => {
  if (actionItemsInterval) clearInterval(actionItemsInterval);
});
const fetchTickets = async () => {
  try {
    const response = await axios.get(`${axios.defaults.baseURL}ticket/new`);
    totalTickets.value = response.data.map(ticket => {
      ticket.client = ticket.client || { companyname: '' };
      if (ticket.contact && ticket.contact.name) {
        // Prefer the contact's user_type; fall back to the ticket's stored
        // requester_user_type. Only append it when present (never "(undefined)").
        const userType = ticket.contact.user_type || ticket.requester_user_type;
        ticket.requester = userType
          ? ticket.contact.name + ' (' + userType + ')'
          : ticket.contact.name;
      }
      if (ticket.promised && ticket.promised !== '0000-00-00 00:00:00') {
        ticket.promised = formatDateTimeLocal(ticket.promised);
      } else {
        const now = new Date();

        const todayNoon = new Date(
          now.getFullYear(),
          now.getMonth(),
          now.getDate(),
          12, 0, 0
        );

        // Convert to "YYYY-MM-DD HH:mm:ss"
        const yyyy = todayNoon.getFullYear();
        const mm = String(todayNoon.getMonth() + 1).padStart(2, '0');
        const dd = String(todayNoon.getDate()).padStart(2, '0');
        const hh = String(todayNoon.getHours()).padStart(2, '0');
        const min = String(todayNoon.getMinutes()).padStart(2, '0');
        const ss = String(todayNoon.getSeconds()).padStart(2, '0');

        const formatted = `${yyyy}-${mm}-${dd} ${hh}:${min}:${ss}`;

        ticket.promised = formatDateTimeLocal(formatted);
      }
      if (ticket.deadline) {
        ticket.deadline = formatDateTimeLocal(ticket.deadline);
      }
      if (ticket.userrequesttimeframe_id) {
        ticket.userrequesttimeframe_id = ticket.userrequesttimeframe_id || 0;
      }
      // Prefer the linked contact's canonical email/phone; if the contact record
      // doesn't carry one, keep whatever the ticket itself stored (fallback).
      if (ticket.contact && ticket.contact.email && ticket.contact.email.length > 0) {
        ticket.email = ticket.contact.email;
      }
      if (ticket.contact && ticket.contact.phone_primary && ticket.contact.phone_primary.length > 0) {
        ticket.alternatephone = ticket.contact.phone_primary;
      }

      if (!ticket.requester && ticket.email) {
        const normalizedTicketEmail = ticket.email?.trim().toLowerCase();
        const foundRequester = requesters.value.find(requester =>
          requester.email?.trim().toLowerCase() === normalizedTicketEmail
        );

        if (foundRequester) {
          // Carry the matched user's id (and whether they are internal staff) so the
          // submit goes down the "existing contact" path. Sending only the name with
          // contact_id 0 made the backend re-resolve the person by email and link an
          // internal colleague to the client company as if they were a new contact.
          const label = foundRequester.name + ' (' + foundRequester.user_type + ')';
          if (foundRequester.is_internal) {
            ticket.requesterInt = label;
          } else {
            ticket.requester = label;
          }
          ticket.contact_id = foundRequester.id;
        }
      }
      if (ticket.description) {
        ticket.originalDescription = ticket.description;
      }
      // Set up the ticket's editing-state fields once, up front, so they exist
      // before first render and are never reset by a recompute.
      initTicketState(ticket);
      return ticket;
    });
  } catch (error) {
    console.error("Error fetching tickets in delegator view:", error);
  }
};
const fetchActionItems = async () => {
  try {
    const res = await axios.get(`user/actionitems`);
    const data = res.data || {};
    leaveFuture.value = data.leaveFuture || [];
    leaveToday.value = data.leaveToday || [];
  } catch (err) {
    console.error("Failed to fetch action items / leave data", err);
  }
};
function handleClose() {
  showModal.value = false;
  console.log(showModal.value);
}

const requestTypes = ref([]);

// Pure getter for the ticket currently in view.
//
// IMPORTANT: keep this free of side effects. This computed used to initialize
// the ticket's editing fields AND fire off axios calls on every recompute —
// which meant any reactive change (typing a description, changing the client,
// etc.) silently reset in-progress edits: the selected ticket type, client
// location, watchers, request assignees, and the documents-marked-for-delete
// list. Per-ticket field setup now happens once in fetchTickets(); async,
// company-scoped data is loaded by the watcher below, which fires only when the
// ticket OBJECT changes (page navigation / reload), not on property edits.
const currentTicket = computed(() => {
  const index = (currentPage.value - 1) * ticketsPerPage;
  return totalTickets.value[index];
});

// Tracks which ticket objects have had their editing state initialized. Kept in
// a WeakSet (keyed on the object reference) rather than a flag on the ticket so
// we don't add an enumerable property — the submit handler serializes every
// ticket key into the delegate payload, and we don't want to leak a bookkeeping
// field to the backend.
const initializedTickets = new WeakSet();

// One-time initialization of a ticket's editing state. Called from
// fetchTickets() as each ticket is loaded, so the fields exist before first
// render. Guarded so it never clobbers fields the user is editing.
const initTicketState = (ticket) => {
  if (!ticket || initializedTickets.has(ticket)) return;
  ticket.type = ticket.ticket_type && ticket.ticket_type.description
    ? ticket.ticket_type.description
    : "";
  ticket.clientLocation = ticket.clientLocation ?? "";
  ticket.attachTo = ticket.attachTo ?? 0;
  ticket.deleteDocumentIds = ticket.deleteDocumentIds ?? [];
  ticket.extReqEmail = ticket.extReqEmail ?? "";
  ticket.requesterInt = ticket.requesterInt ?? "";
  ticket.oldRequester = ticket.oldRequester ?? '';
  ticket.reqAssignees = ticket.reqAssignees ?? [];
  ticket.watcherId = ticket.watcherId ?? "";
  ticket.requestAssigneeId = ticket.requestAssigneeId ?? "";
  ticket.projects = ticket.projects ?? [];
  ticket.documents = ticket.documents ?? [];
  ticket.additionalAddresses = ticket.additionalAddresses ?? [];
  if (!Array.isArray(ticket.watchers)) ticket.watchers = [];
  initializedTickets.add(ticket);
};

// Documents belong to a specific ticket, so this is keyed on the ticket id.
const loadTicketDocuments = (ticket) => {
  if (!ticket || !ticket.id) return;
  axios
    .get(`${axios.defaults.baseURL}documents/${ticket.id}`)
    .then((response) => {
      ticket.documents = (response.data || []).map((doc) => ({
        ...doc,
        blacklisted: doc.blacklisted !== undefined ? doc.blacklisted : false
      }));
    })
    .catch((error) => {
      console.error("There was an error fetching documents in a ticket:", error);
    });
};

// Company-scoped lists. Reloaded when the current ticket changes or when the
// user picks a different client (setClient) — never on unrelated recomputes.
const loadCompanyData = (ticket) => {
  if (!ticket || !ticket.company_id) return;
  const companyId = ticket.company_id;
  axios
    .get(`${axios.defaults.baseURL}projects/${companyId}`)
    .then((response) => { ticket.projects = response.data; })
    .catch((error) => {
      console.error("There was an error fetching projects related to a company:", error);
    });
  axios
    .get(`${axios.defaults.baseURL}user/active/${companyId}`)
    .then((response) => { employees.value = response.data; })
    .catch((error) => {
      console.error("There was an error fetching active users for a company:", error);
    });
  axios
    .get(`${axios.defaults.baseURL}ticket/get-types/${companyId}`)
    .then((response) => { requestTypes.value = response.data; })
    .catch((error) => {
      console.error("There was an error fetching ticket types:", error);
    });
  axios
    .get(`${axios.defaults.baseURL}clients/locations`, { params: { client_id: companyId } })
    .then((response) => { ticket.additionalAddresses = response.data; })
    .catch((error) => {
      console.error("There was an error fetching locations:", error);
    });
  axios
    .get(`${axios.defaults.baseURL}clients/notes`, { params: { id: companyId } })
    .then((response) => { clientNotes.value = response.data; })
    .catch((error) => {
      console.error("There was an error fetching notes:", error);
    });
  // Drop the previous client's people now, so the requester typeahead doesn't rank
  // them first for the new client while this list is still loading.
  allRequesters.value = [];
  axios
    .get(`${axios.defaults.baseURL}requesters/${companyId}`)
    .then((response) => { allRequesters.value = response.data; })
    .catch((error) => {
      console.error("There was an error fetching requesters:", error);
    });
};

// Load async data whenever the visible ticket changes — initial load,
// pagination between queued tickets, or a full reload. Fires on the object
// reference changing, NOT on property edits to the same ticket, so it no longer
// stomps on the user's in-progress edits.
watch(currentTicket, (ticket) => {
  if (!ticket) return;
  loadTicketDocuments(ticket);
  loadCompanyData(ticket);
}, { immediate: true });
function getListByField(field) {
  switch (field) {
    case 'client':
      return filteredClients.value;
    case 'requester':
      return filteredRequesters.value;
    case 'internalRequester':
      return filteredIntRequesters.value;
    case 'watcher':
      return filteredWatchers.value;
    case 'type':
      return filteredReqTypes.value;
    case 'tech':
      return filteredAssignees.value;
    default:
      return [];
  }
}


const handleEditJob = (userId) => {
  // "Jobs of a specific tech" is restricted to internal admins (roles 10/20/30) —
  // matches the router guard and the backend gate. Non-admins clicking the tech
  // name cell get no navigation (the name still displays as data).
  if (!userroles.value.some(r => [10, 20, 30].includes(Number(r)))) {
    return;
  }
  const routeData = router.resolve({ name: 'userJobsList', params: { userId } });
  window.open(routeData.href, '_blank');
};
const getAuthor = (job) => {
  // If user_id is 0, use contact
  if (job.user_id === 0 && job.contact) {
    return `${job.contact.name_last ?? ''} ${(job.contact.name_first ?? '')}`;
  }

  // Otherwise, use userProfile
  if (job.user_profile) {
    return `${job.user_profile.lastname ?? ''} ${(job.user_profile.firstname ?? '')}`;
  }

  // Fallback
  return 'N/A';
};

const onKeyDown = (field, direction) => {
  const state = dropdownState[field];
  const list = getListByField(field);

  if (!state.visible || !list || list.length === 0) return;

  if (direction === 'down') {
    state.index = (state.index + 1) % list.length;
  } else if (direction === 'up') {
    state.index = (state.index - 1 + list.length) % list.length;
  }
};

const onEnter = (field, selectFn, nextRef = null, ...args) => {
  const state = dropdownState[field];
  const list = getListByField(field);

  if (state.index >= 0 && state.index < list.length) {
    selectFn(list[state.index], ...args);
    state.index = -1;
    if (nextRef && nextRef.value) {
      nextTick(() => nextRef.value.focus());
    }
  }
};

const resetDropdownState = (field) => {
  dropdownState[field].index = -1;
  dropdownState[field].visible = false;
};

const dropdownItemRefs = reactive({
  client: [],
  type: [],
  requester: [],
  internalRequester: [],
  watcher: [],
  tech: []
});

const setDropdownItemRef = (el, index, field) => {
  if (el) dropdownItemRefs[field][index] = el;
};

['client', 'type', 'requester', 'internalRequester', 'watcher', 'tech'].forEach(field => {
  watch(() => dropdownState[field].index, async (newIndex) => {
    const el = dropdownItemRefs[field][newIndex];
    await nextTick();
    if (el && typeof el.scrollIntoView === 'function') {
      el.scrollIntoView({ block: 'nearest' });
    }
  });
});

const isImage = (mimeType) => {
  // Check if the MIME type represents an image
  return mimeType.startsWith("image/");
};

const generateThumbnailUrl = (s3objectname) => {
  // Assuming the URL pattern is consistent
  return `https://atms.s3.amazonaws.com/${s3objectname}`;
};

const totalPages = computed(() => {
  return Math.ceil(totalTickets.value.length / ticketsPerPage);
});

const isCoManagedTech = computed(() => {
  return userroles.value.includes(80);
});

// Methods to change pages
const changePage = (page) => {
  if (page >= 1 && page <= totalPages.value) {
    currentPage.value = page;
  }
};

const uploadedFile = ref(null);
const uploadedDocuments = ref([
  { name: "ProjectProposal.pdf", type: "PDF", id: 1 },
  { name: "ExpenseReport.xlsx", type: "Spreadsheet", id: 2 },
  { name: "DesignMockup.sketch", type: "Design", id: 3 },
  { name: "MeetingNotes.txt", type: "Text", id: 4 },
]);
const fileInput = ref(null);

const handleFileUpload = (event) => {
  const files = Array.from(event.target.files || []);
  if (!files.length) return;
  uploadedFile.value = files[files.length - 1];

  // One doc entry per selected file; +index keeps ids unique within a pick.
  const newDocuments = files.map((file, index) => ({
    id: Date.now() + index, // Mock ID, using timestamp to simulate uniqueness
    doc_name: file.name,
    mime: file.type,
    size: file.size, // Optional, but useful for display
    blacklisted: false,
    isNew: true,
    file: file,
    is_image: file.type?.startsWith('image/'),
    view_url: file.type?.startsWith('image/') ? URL.createObjectURL(file) : '',
    download_url: ''
  }));

  // Directly push the new documents into the currentTicket's documents array
  if (currentTicket.value && currentTicket.value.documents) {
    currentTicket.value.documents.push(...newDocuments);
  } else {
    // Initialize the documents array if it doesn't exist
    currentTicket.value = { ...currentTicket.value, documents: newDocuments };
  }
  if (fileInput.value) {
    fileInput.value.value = ''; // Resets the file input element
  }
};

// Blacklisting removes the file from this ticket and stores its content hash;
// identical attachments on future inbound emails are skipped by the backend.
const showBlacklist = ref(false);
const blacklistLoading = ref(false);
const blacklistEntries = ref([]);

const loadBlacklist = async () => {
  blacklistLoading.value = true;
  try {
    const response = await axios.get(`${axios.defaults.baseURL}documents/blacklist`);
    blacklistEntries.value = response.data || [];
  } catch (error) {
    console.error("There was an error fetching the attachment blacklist:", error);
  } finally {
    blacklistLoading.value = false;
  }
};

const toggleBlacklistPanel = () => {
  showBlacklist.value = !showBlacklist.value;
  if (showBlacklist.value) loadBlacklist();
};

const blacklistDocument = async (doc) => {
  if (!currentTicket.value?.id || doc.isNew) return;
  if (!confirm(`Blacklist "${doc.doc_name}"?\n\nIt will be removed from this ticket, and identical files attached to future emails will be skipped.`)) {
    return;
  }
  doc.blacklisted = true;
  try {
    await axios.post(`${axios.defaults.baseURL}documents/blacklist`, {
      ticket_id: currentTicket.value.id,
      document_id: doc.id,
    });
    currentTicket.value.documents = currentTicket.value.documents.filter(d => d.id !== doc.id);
    if (showBlacklist.value) loadBlacklist();
  } catch (error) {
    // The global axios interceptor shows the backend message.
    doc.blacklisted = false;
  }
};

const unblacklist = async (entry) => {
  if (!confirm(`Remove "${entry.doc_name}" from the blacklist? Future emails with this file will keep it again.`)) {
    return;
  }
  try {
    await axios.delete(`${axios.defaults.baseURL}documents/blacklist/${entry.id}`);
    blacklistEntries.value = blacklistEntries.value.filter(e => e.id !== entry.id);
  } catch (error) {
    // Shown by the global axios interceptor.
  }
};

const deleteDocument = (document) => {
  // TODO: Add api call logic to delete the document from the server
  // and remove it from the uploadedDocuments array
  if (currentTicket.value && currentTicket.value.documents) {
    currentTicket.value.documents = currentTicket.value.documents.filter(doc => doc.id !== document.id);
    if (!currentTicket.value.deleteDocumentIds) {
      currentTicket.value.deleteDocumentIds = [];
    }
    currentTicket.value.deleteDocumentIds.push(document.id);
  }
};

const validateTicket = (ticket, startJob = false) => {
  const missingFields = [];

  if (!ticket.company_id) {
    missingFields.push({ field: 'company_id', label: 'Client' });
  }
  if (!ticket.email) {
    missingFields.push({ field: 'email', label: 'Email' });
  }
  // if (!ticket.alternatephone) {
  //   missingFields.push({ field: 'alternatephone', label: 'Phone' });
  // }

  if (!ticket.tickettype_id && ticket.attachTo == 0) {
    missingFields.push({ field: 'tickettype_id', label: 'Ticket Type' });
  }

  if (!ticket.promised) {
    missingFields.push({ field: 'promised', label: 'Promised Date' });
  }

  if (!ticket.requester && !ticket.requesterInt) {
    missingFields.push({ field: 'requester', label: 'Requester required' });
  }

  if (!ticket.tech_id && !startJob && ticket.attachTo == 0) {
    missingFields.push({ field: 'tech_id', label: 'Assigned Technician' });
  }

  return missingFields;
};
const delegateTicket = async (ticket, startJob) => {
  // Check if description has been cleared
  if (ticket.originalDescription && !ticket.description) {
    if (!confirm("There was a description, it is now blank, do you wish to continue?")) {
      return;
    }
  }

  // Check if the ticket is marked as urgent
  if (ticket.urgent) {
    if (!confirm("Marked URGENT! The technician will be paged. Would you like to continue?")) {
      return;
    }
  }

  const missing = validateTicket(ticket, startJob);
  if (missing.length > 0) {
    alert(`Missing Required Fields:\n${missing.map(f => `• ${f.label}`).join('\n')}`);
    return;
  }

  if (!ticket.requester && ticket.requesterInt) {
    ticket.requester = ticket.requesterInt;
  }
  proxyInstance.$updateLoading(1);
  // Proceed with delegation
  try {
    const formData = new FormData();
    Object.keys(ticket).forEach(key => {
      if (key !== 'documents' || key !== 'deleteDocumentIds' || key != 'watchers' || key != 'reqAssignees') {  // Exclude the documents from regular appending
        formData.append(key, ticket[key]);
      }
    });
    // Append documents
    ticket.documents.forEach(doc => {
      if (doc.isNew) {
        formData.append('ticketdoc[]', doc.file, doc.file.name);
      }
    });

    ticket.deleteDocumentIds.forEach(doc => {
      formData.append('deleteDocumentIds[]', doc);
    });
    ticket.watchers.forEach(watcher => {
      formData.append('watchers[]', JSON.stringify({ id: watcher.id, visible: watcher.visible }));
    });
    ticket.reqAssignees.forEach(reqAssignee => {
      formData.append('reqAssignees[]', reqAssignee.id);
    });
    for (let [key, value] of formData.entries()) {
      console.log(`${key}: ${value}`);
    }
    formData.append('startJob', startJob);
    const response = await axios.post(`${axios.defaults.baseURL}ticket/delegate`, formData);
    if (response.status === 200) {
      // If delegation is successful, remove the ticket from the totalTickets
      // Remove the ticket from totalTickets
      const index = totalTickets.value.findIndex(t => t.id === ticket.id);
      if (index !== -1) {
        totalTickets.value.splice(index, 1);
      }

      // Adjust currentPage and recalculate totalPages
      const totalPages = Math.ceil(totalTickets.value.length / ticketsPerPage);
      if (currentPage.value > totalPages) {
        currentPage.value = totalPages > 0 ? totalPages : 1;
      }

      if (startJob && response.data.ticketId) {
        const routeData = router.resolve({ name: 'addEditJob', query: { ticketId: response.data.ticketId } });
        window.open(routeData.href, '_blank');
      }
    } else {
      alert('Failed to delegate ticket.');
    }
  } catch (error) {
    console.error("There was an error delegating the ticket:", error);
    alert('Error delegating ticket: ' + (error?.message || 'Unexpected error occurred.'));
  } finally {
    proxyInstance.$updateLoading(-1);
  }

};

const deleteTicket = (ticketId) => {
  // Confirmation dialog before deleting
  if (!confirm('Are you sure you want to delete this ticket?')) {
    return;
  }
  proxyInstance.$updateLoading(1);

  axios.post(`${axios.defaults.baseURL}ticket/delete`, { delete: ticketId })
    .then(response => {
      alert('Ticket deleted successfully');
      const index = totalTickets.value.findIndex(t => t.id === ticketId);
      if (index !== -1) {
        totalTickets.value.splice(index, 1);
      }
      const totalPages = Math.ceil(totalTickets.value.length / ticketsPerPage);
      if (currentPage.value > totalPages) {
        currentPage.value = totalPages > 0 ? totalPages : 1;
      }
    })
    .catch(error => {
      console.error('There was an error deleting the ticket:', error);
    })
    .finally(() => {
      proxyInstance.$updateLoading(-1);
    });
};
const showTextArea = ref(false);
const activeTextarea = ref(null);

const handleTextCompletion = ({ updatedText, key }) => {
  const fieldMapping = {
    requester: 'description',
    aimNotesPublic: 'delegatornotes',
    privateNotes: 'privateinfo'
  };
  const dbField = fieldMapping[key]; // Get the database field name from the mapping
  const oldText = currentTicket.value[dbField]; // Use the correct field name to get the old value

  // Check if the text actually changed before making an API call
  if (updatedText !== oldText) {
    currentTicket.value[dbField] = updatedText; // Update the value in the current ticket
    changedNotes(dbField); // Call the helper function to update the database
  }

  // Close the modal and reset the active textarea identifier
  showTextArea.value = false;
  activeTextarea.value = null;
};

const changedNotes = (notes) => {
  if (!currentTicket.value) return;
  const postData = {
    ticketId: currentTicket.value.id,
    field: notes,
    value: currentTicket.value[notes]
  };
  axios.post('/ticket/update-notes', postData)
    .then(response => {
      console.log('Notes updated successfully:', response.data);
    })
    .catch(error => {
      console.error('Error updating notes:', error);
    });
}

const getTextareaInitialText = () => {
  switch (activeTextarea.value) {
    case "requester":
      return currentTicket.value.description;
    case "aimNotesPublic":
      return currentTicket.value.delegatornotes;
    case "privateNotes":
      return currentTicket.value.privateinfo;
    default:
      return "";
  }
};



const activateTextArea = (key) => {
  activeTextarea.value = key;
  showTextArea.value = true;
};

// const searchReqTypeQuery = ref(currentTicket.value.type);
const showReqTypeDropdown = ref(false);

const filteredReqTypes = computed(() => {
  return currentTicket.value.type
    ? requestTypes.value.filter((e) =>
      e.description
        .toLowerCase()
        .includes(currentTicket.value.type.toLowerCase())
    )
    : requestTypes.value;
});

const setReqType = (type) => {
  currentTicket.value.tickettype_id = type.id;
  currentTicket.value.type = type.description;
  // Keep the nested ticket_type in sync — the currentTicket computed re-derives
  // `type` from `ticket_type.description` on every recompute (e.g. when the client
  // changes), so without this the selection would revert to the original value.
  currentTicket.value.ticket_type = { id: type.id, description: type.description };
  dropdownState.type.visible = false;
  dropdownState.type.index = -1;
};

const clearReqType = () => {
  currentTicket.value.tickettype_id = '';
  currentTicket.value.type = '';
  // Clear the source object too, otherwise the computed re-derives the old type.
  currentTicket.value.ticket_type = null;
};


const showClientDropdown = ref(false);

const filteredClients = computed(() => {
  return currentTicket.value.client.companyname
    ? clients.value.filter((e) =>
      e.companyname
        .toLowerCase()
        .includes(currentTicket.value.client.companyname.toLowerCase())
    )
    : clients.value;
});

const setClient = (client) => {
  currentTicket.value.company_id = client.id;
  currentTicket.value.client.companyname = client.companyname;
  // Reload the lists scoped to the newly selected client (types, projects,
  // requesters, locations, notes). This does NOT reset the user's current
  // selections — same behaviour as the Create/Edit Ticket screens.
  loadCompanyData(currentTicket.value);
  dropdownState.client.visible = false;
  dropdownState.client.index = -1;
};

const clearClient = () => {
  currentTicket.value.company_id = '';
  currentTicket.value.client.companyname = '';
};
const allRequesters = ref([]);
// System-wide requester list, loaded lazily the first time the requester field
// is focused. Used as the search source so typing matches requesters at ANY
// client, while an empty focus still shows only the selected client's list.
const allSystemRequesters = ref([]);
let allRequestersLoaded = false;
const ensureAllRequestersLoaded = () => {
  if (allRequestersLoaded) return;
  allRequestersLoaded = true;
  axios
    .get(`${axios.defaults.baseURL}requesters/`)
    .then((response) => { allSystemRequesters.value = response.data; })
    .catch((error) => {
      allRequestersLoaded = false; // allow a retry on the next focus
      console.error("There was an error fetching all requesters:", error);
    });
};
const onRequesterFocus = () => {
  dropdownState.requester.visible = true;
  ensureAllRequestersLoaded();
};
const requesters = ref([
]);

const showReqDropdown = ref(false);

const filteredRequesters = computed(() => {
  const q = (currentTicket.value?.requester || '').trim().toLowerCase();
  if (q) {
    // Typing: search across ALL requesters system-wide. Fall back to the
    // client-scoped list until the full list has finished loading.
    const source = allSystemRequesters.value.length ? allSystemRequesters.value : allRequesters.value;
    // The selected client's own people are listed first.
    return searchRequesters(source, q, currentTicket.value?.company_id, { clientList: allRequesters.value });
  }
  // Empty field: show this client's requesters when a client is selected,
  // otherwise the full system-wide list.
  return currentTicket.value?.company_id ? allRequesters.value : allSystemRequesters.value;
});

const selectedRequester = ref(null);
const confirmRequesterChange = (req, isInternal = false) => {
  if (currentTicket.value.contact_id && currentTicket.value.contact_id != req.id) {
    selectedRequester.value = req;
    console.log('1');
    // Find the old requester by contact_id
    const oldRequester = requesters.value.find(r => r.id === currentTicket.value.contact_id);
    if (oldRequester) {
      currentTicket.value.oldRequester = { id: oldRequester.id, name: oldRequester.name, visible: true };
    } else {
      currentTicket.value.oldRequester = { id: currentTicket.value.contact_id, name: currentTicket.value.requester, visible: true };
    }
    showReqConfirmationModal.value = true;
    selectedRequester.value.isInternal = isInternal;
  } else {
    console.log('2');
    setReq(req, isInternal);
  }
};


const handleReqConfirmation = (addOldRequesterAsWatcher) => {
  if (addOldRequesterAsWatcher) {
    currentTicket.value.watchers.push(currentTicket.value.oldRequester);
  }
  const req = selectedRequester.value;
  setReq(req, req.isInternal || false);
  // setReq(selectedRequester.value);
  showReqConfirmationModal.value = false;
};
const setReq = (req, isInternal = false) => {
  if (isInternal) {
    currentTicket.value.requester = '';
    currentTicket.value.requesterInt = req.name + ' (' + req.user_type + ')';
  } else {
    currentTicket.value.requester = req.name + ' (' + req.user_type + ')';
    currentTicket.value.requesterInt = '';
  }
  currentTicket.value.contact_id = req.id;
  currentTicket.value.email = req.email;
  // searchReqTypeQuery.value = type.description;
  // showReqDropdown.value = false;
  dropdownState.requester.visible = false;
  dropdownState.internalRequester.visible = false;
  dropdownState.requester.index = -1;
  dropdownState.internalRequester.index = -1;
  currentTicket.value.alternatephone = req.phone_primary ? req.phone_primary : req.phone_secondary ? req.phone_secondary : ''; // Reset phone
  currentTicket.value.extReqEmail = "";
};

const clearReq = () => {
  currentTicket.value.requester = '';
  currentTicket.value.contact_id = '';
  currentTicket.value.email = '';
  currentTicket.value.alternatephone = ''; // Reset phone
  currentTicket.value.extReqEmail = "";
};

const clearIntReq = () => {
  currentTicket.value.requesterInt = '';
  currentTicket.value.contact_id = '';
  currentTicket.value.email = '';
  currentTicket.value.alternatephone = ''; // Reset phone
  currentTicket.value.extReqEmail = "";
};


const showIntReqDropdown = ref(false);

const filteredIntRequesters = computed(() => {
  return currentTicket.value.requesterInt
    ? intRequesters.value.filter((e) =>
      e.name
        .toLowerCase()
        .includes(currentTicket.value.requesterInt.toLowerCase())
    )
    : intRequesters.value;
});

const setIntReq = (req) => {
  currentTicket.value.requesterInt = req.name;
  currentTicket.value.contact_id = req.id;
  currentTicket.value.requester = "";
  currentTicket.value.email = req.email ? req.email : "";
  currentTicket.value.alternatephone = req.phone_primary
    ? req.phone_primary
    : req.phone_secondary
      ? req.phone_secondary
      : "";
  // searchReqTypeQuery.value = type.description;
  showIntReqDropdown.value = false;
  currentTicket.value.extReqEmail = "";
};

const showAssignDropdown = ref(false);

const filteredAssignees = computed(() => {
  return currentTicket.value.assign
    ? employees.value.filter((e) =>
      e.name.toLowerCase().includes(currentTicket.value.assign.toLowerCase())
    )
    : employees.value;
});

const setAssignee = (assignee) => {
  currentTicket.value.assign = assignee.name;
  currentTicket.value.tech_id = assignee.id;
  // searchReqTypeQuery.value = type.description;
  dropdownState.tech.visible = false;
  dropdownState.tech.index = -1;
};

const clearAssignee = () => {
  currentTicket.value.assign = '';
  currentTicket.value.tech_id = '';
};


const closeReqDropdown = () => {
  // showReqDropdown.value = false;
  dropdownState.requester.visible = false;
  dropdownState.requester.index = -1;
  dropdownItemRefs.requester = [];
}
const closeWatcherDropdown = () => {
  // showWatcherDropdown.value = false;
  dropdownState.watcher.visible = false;
  dropdownState.watcher.index = -1;
  dropdownItemRefs.watcher = [];
}
const closeReqAssigneeDropdown = () => {
  showRequestAssigneeDropdown.value = false;
}
const closeAssignDropdown = () => {
  // showAssignDropdown.value = false;
  dropdownState.tech.visible = false;
  dropdownState.tech.index = -1;
  dropdownItemRefs.tech = [];
}
const closeIntReqDropdown = () => {
  // showIntReqDropdown.value = false;
  dropdownState.internalRequester.visible = false;
  dropdownState.internalRequester.index = -1;
  dropdownItemRefs.internalRequester = [];
}
const closeClientDropdown = () => {
  // showClientDropdown.value = false;
  dropdownState.client.visible = false;
  dropdownState.client.index = -1;
  dropdownItemRefs.client = [];
}
const closeTypeDropdown = () => {
  // showReqTypeDropdown.value = false;
  dropdownState.type.visible = false;
  dropdownState.type.index = -1;
  dropdownItemRefs.type = [];
}

const toggleWatcher = () => {
  showWatcher.value = !showWatcher.value;
}
const toggleRequesterAssignee = () => {
  showRequestAssignee.value = !showRequestAssignee.value;
}

const updatePromisedTime = () => {
  if (!currentTicket.value) return

  const existing = currentTicket.value.promised
  let dateStr

  if (existing && typeof existing === 'string' && existing.includes('T')) {
    dateStr = existing.split('T')[0]
  } else {
    const today = new Date()
    const year = today.getFullYear()
    const month = String(today.getMonth() + 1).padStart(2, '0')
    const day = String(today.getDate()).padStart(2, '0')
    dateStr = `${year}-${month}-${day}`
  }

  currentTicket.value.promised = `${dateStr}T${selectedTime.value}`
}

const updatePromisedDate = () => {
  if (!currentTicket.value) return

  const baseDate = new Date()
  const dayOffset = parseInt(selectedDay.value, 10) || 0
  baseDate.setDate(baseDate.getDate() + dayOffset)

  const year = baseDate.getFullYear()
  const month = String(baseDate.getMonth() + 1).padStart(2, '0')
  const day = String(baseDate.getDate()).padStart(2, '0')
  const dateStr = `${year}-${month}-${day}`

  let timeStr = '12:00'
  const existing = currentTicket.value.promised

  if (existing && typeof existing === 'string' && existing.includes('T')) {
    timeStr = existing.split('T')[1] || '12:00'
  } else if (selectedTime.value !== '-1') {
    timeStr = selectedTime.value
  }

  currentTicket.value.promised = `${dateStr}T${timeStr}`
}

// Watchers to react to changes in 'selectedDay' or 'selectedTime'
watch(selectedDay, () => {
  if (!currentTicket.value) return
  if (selectedDay.value === '-1') return

  if (currentTicket.value.userrequesttimeframe_id) {
    currentTicket.value.userrequesttimeframe_id = 0
  }

  updatePromisedDate()
}, {
  immediate: true
});

watch(selectedTime, () => {
  if (!currentTicket.value) return
  if (selectedTime.value === '-1') return

  if (currentTicket.value.userrequesttimeframe_id) {
    currentTicket.value.userrequesttimeframe_id = 0
  }

  updatePromisedTime()
}, {
  immediate: true
});
const formatForDatetimeLocal = (date) => {
  const d = date.toLocaleDateString('en-CA'); // YYYY-MM-DD
  const t = date.toLocaleTimeString('en-CA', { hour12: false, hour: '2-digit', minute: '2-digit' }); // HH:mm
  return `${d}T${t}`;
};
const setPromisedDateTime = (daysOffset, time) => {
  const promisedDate = new Date();
  promisedDate.setDate(promisedDate.getDate() + daysOffset);

  // Convert to local timezone
  const [hours, minutes] = time.split(':');
  promisedDate.setHours(hours, minutes, 0, 0);
  currentTicket.value.promised = formatForDatetimeLocal(promisedDate);
};
const changeUserRequestTimeframe = (val) => {
  const newVal = String(val)

  if (!timeframesMapping.value[newVal]) {
    return
  }
  selectedDay.value = '-1'
  selectedTime.value = '-1'

  const mapping = timeframesMapping.value[newVal]

  let days = mapping.days
  const time = mapping.time

  if (newVal === '8' || newVal === '4') {
    days = getNextBusinessDayOffset()
  }
  setPromisedDateTime(days, time)
}


const showAddUserModal = ref(false);
const handleAddUserCompletion = ({ newUser }) => {
  console.log(newUser.email)
  currentTicket.value.requester = newUser.firstName + ' ' + newUser.lastName;
  currentTicket.value.email = newUser.email;
  currentTicket.value.alternatephone = newUser.phone;
  currentTicket.value.contact_id = 0;
  // call the api that adds the user
  // call the api to get updated list of requesters
  // Close the modal and reset the active textarea identifier
  showAddUserModal.value = false;
};

const handleFocus = (field) => {
  currentTicket.value['old' + field] = currentTicket.value[field];
  console.log(currentTicket.value['old' + field])
  showConfirmationModal.value = false;
}
const handleBlur = (field) => {
  if (currentTicket.value[field]) {
    fieldToConfirm.value = field;
    valueToConfirm.value = currentTicket.value[field];
    showConfirmationModal.value = true;
  }
}

const handleUpdateUserConfirm = (data) => {
  // Logic to handle the confirmation action
  console.log('return to handleUpdateUserConfirm, make a post api call to update');
  showConfirmationModal.value = false;
}
const handleUpdateUserCancel = (data) => {
  // Logic to handle the cancellation action
  console.log(data.field)
  currentTicket.value[data.field] = currentTicket.value['old' + data.field]
  showConfirmationModal.value = false;
  // if (fieldToConfirm.value === 'email') {
  //   currentTicket.value.email = '';
  // } else if (fieldToConfirm.value === 'alternatephone') {
  //   currentTicket.value.alternatephone = '';
  // }
}

const showWatcherDropdown = ref(false);
const showRequestAssigneeDropdown = ref(false);

// Computed properties for filtered watchers and assignees based on the input
const filteredWatchers = computed(() => {
  // Typing: system-wide search, the selected client's own people first.
  return watchersInput.value
    ? searchRequesters(requesters.value, watchersInput.value, currentTicket.value?.company_id, { clientList: allRequesters.value })
    : allRequesters.value;
});

const filteredRequestAssignees = computed(() => {
  // Typing: system-wide search, the selected client's own people first.
  return reqAssigneesInput.value
    ? searchRequesters(requesters.value, reqAssigneesInput.value, currentTicket.value?.company_id, { clientList: allRequesters.value })
    : allRequesters.value;
});

// Methods for setting watcher and assignee
const addWatcher = (watcher) => {
  if (!currentTicket.value.watchers.some(w => w.id === watcher.id)) {
    currentTicket.value.watchers.push({ id: watcher.id, name: watcher.name, visible: true });
  }
  // showWatcherDropdown.value = false;
  dropdownState.watcher.visible = false;
  dropdownState.watcher.index = -1;
  watchersInput.value = '';
};

const addRequestAssignee = (assignee) => {
  if (!currentTicket.value.reqAssignees.some(a => a.id === assignee.id)) {
    currentTicket.value.reqAssignees.push({ id: assignee.id, name: assignee.name });
  }
  showRequestAssigneeDropdown.value = false;
  reqAssigneesInput.value = '';
};

const removeWatcher = (watcher) => {
  const watcherIndex = currentTicket.value.watchers.findIndex(w => w.id === watcher.id);
  if (watcherIndex !== -1) {
    currentTicket.value.watchers[watcherIndex].visible = false;
  }
};

const removeRequestAssignee = (assignee) => {
  currentTicket.value.reqAssignees = currentTicket.value.reqAssignees.filter(a => a.id !== assignee.id);
};

// Methods for toggling the dropdown visibility
const toggleWatcherDropdown = () => {
  showWatcherDropdown.value = !showWatcherDropdown.value;
};

const toggleRequestAssigneeDropdown = () => {
  showRequestAssigneeDropdown.value = !showRequestAssigneeDropdown.value;
};

// Private/internal tickets never email the requester or watchers (backend rule),
// so Notify is shown off and disabled while either box is checked.
const notifyBlocked = computed(() => currentTicket.value.unpublished === 1 || currentTicket.value.internal === 1);

const notifyCheckboxModel = computed({
  get() {
    return !notifyBlocked.value && currentTicket.value.emailnotification === 1;
  },
  set(newValue) {
    currentTicket.value.emailnotification = newValue ? 1 : 0;
  }
});

const unPublishedCheckboxModel = computed({
  get() {
    return currentTicket.value.unpublished === 1;
  },
  set(newValue) {
    currentTicket.value.unpublished = newValue ? 1 : 0;
    if (newValue) currentTicket.value.emailnotification = 0;
  }
});

const internalCheckboxModel = computed({
  get() {
    return currentTicket.value.internal === 1;
  },
  set(newValue) {
    currentTicket.value.internal = newValue ? 1 : 0;
    if (newValue) currentTicket.value.emailnotification = 0;
  }
});

const urgencyCheckboxModel = computed({
  get() {
    return currentTicket.value.urgency === 1;
  },
  set(newValue) {
    currentTicket.value.urgency = newValue ? 1 : 0;
  }
});

// Function to handle ticket attachment
function handleRelatedTickets(payload) {
  if (!currentTicket.value) return;

  currentTicket.value.attachTo = payload?.id || 0;
  currentTicket.value.attachedRelatedTicket = payload?.ticket || null;

  showModal.value = false;
}


</script>

<style scoped>
/* Styles are scoped to this component only */
.rounded {
  border-radius: 0.375rem;
  /* 6px */
}

.development {
  background-color: #ccffcc;
}

/* Light Green */
.remote {
  background-color: #ffcccc;
}

/* Light Pink */
.lunch {
  background-color: #ffffcc;
}

/* Light Yellow */
.bench {
  background-color: #93c5fd;
}

.gold {
  background-color: gold;
}

.yellow {
  background-color: #ffffcc;
}

.pink {
  background-color: pink;
}

.lightgreen {
  background-color: lightgreen;
}

.lightblue {
  background-color: rgb(151, 231, 252);
}

.goldenrod {
  background-color: goldenrod;
}

.violet {
  background-color: rgb(223, 144, 247);
}

.d3acd4 {
  background-color: #d3acd4;
}

.ff4d4d {
  background-color: #ff4d4d;
}

.d33A2FF {
  background-color: #33A2FF;
}

/* Light Blue */

.scrollable-requester-list {
  max-height: 400px;
  /* Adjust this value based on your design needs */
  overflow-y: auto;
  /* Enables vertical scrolling */
  border: 1px solid #ccc;
  /* Optional: adds a border around the list */
}

.slide-fade-enter-active,
.slide-fade-leave-active {
  transition: opacity 0.5s ease, transform 0.5s ease;
}

.slide-fade-enter,
.slide-fade-leave-to {
  opacity: 0;
  transform: translateY(-10px);
}
</style>
